/**
 * CategorySelector - Reusable Hierarchical Category Picker
 * 
 * Dropdown component for selecting categories with hierarchical display.
 * Shows parent > child > grandchild structure.
 * 
 * @validates Requirements 5.3 - Support up to 3 levels of hierarchy
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CategoryTreeNode, CategoryBaseType } from '@budgira/shared';
import { useCategoryStore } from '../store/categoryStore';

const ChevronDownIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const FolderIcon = () => (
  <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

interface CategorySelectorProps {
  value?: string; // Category ID
  onChange: (categoryId: string | undefined, categoryName: string | undefined) => void;
  filterType?: CategoryBaseType; // Optional filter by debit/credit
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

interface FlattenedCategory {
  id: string;
  name: string;
  displayName: string; // Full path: "Parent > Child > Grandchild"
  level: number;
  logoUrl?: string;
  baseType: CategoryBaseType;
  hasChildren: boolean; // True if this category has child categories
}

/**
 * Flatten tree for rendering in dropdown
 */
function flattenForDisplay(
  nodes: CategoryTreeNode[],
  path: string[] = [],
  results: FlattenedCategory[] = []
): FlattenedCategory[] {
  for (const node of nodes) {
    const currentPath = [...path, node.name];
    const hasChildren = node.children && node.children.length > 0;
    results.push({
      id: node.id,
      name: node.name,
      displayName: currentPath.join(' › '),
      level: node.hierarchyLevel,
      logoUrl: node.logoUrl,
      baseType: node.baseType,
      hasChildren,
    });
    
    if (hasChildren) {
      flattenForDisplay(node.children, currentPath, results);
    }
  }
  return results;
}

export default function CategorySelector({
  value,
  onChange,
  filterType,
  placeholder,
  className = '',
  disabled = false,
}: CategorySelectorProps) {
  const { t } = useTranslation();
  const { categories, fetchCategories, isLoading } = useCategoryStore();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch categories on mount
  useEffect(() => {
    if (categories.length === 0) {
      fetchCategories();
    }
  }, [categories.length, fetchCategories]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter categories by type if specified
  const filteredCategories = filterType
    ? categories.filter((c) => c.baseType === filterType)
    : categories;

  // Flatten for display (filtered by type)
  const flatCategories = flattenForDisplay(filteredCategories);

  // Flatten ALL categories (unfiltered) to find selected category
  // This ensures we can display a selected category even if it doesn't match the current filter
  const allFlatCategories = flattenForDisplay(categories);

  // Filter by search term
  const searchResults = search
    ? flatCategories.filter((c) =>
        c.displayName.toLowerCase().includes(search.toLowerCase())
      )
    : flatCategories;

  // Find selected category in ALL categories (not just filtered ones)
  const selectedCategory = value
    ? allFlatCategories.find((c) => c.id === value)
    : null;

  const handleSelect = (category: FlattenedCategory) => {
    onChange(category.id, category.name);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    onChange(undefined, undefined);
    setSearch('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? search : (selectedCategory?.displayName || '')}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder || t('categories.selector.placeholder', 'Sélectionner une catégorie...')}
          disabled={disabled}
          className={`w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-slate-900 dark:text-slate-100 ${
            disabled ? 'bg-slate-100 dark:bg-slate-700 cursor-not-allowed' : 'bg-white dark:bg-slate-800'
          }`}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {selectedCategory && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 text-slate-400 hover:text-slate-600"
            >
              ✕
            </button>
          )}
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            className="p-1 text-slate-400"
            disabled={disabled}
          >
            <ChevronDownIcon />
          </button>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-slate-500 dark:text-slate-400">
              <div className="w-5 h-5 border-2 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto" />
            </div>
          ) : searchResults.length === 0 ? (
            <div className="p-4 text-center text-slate-500 dark:text-slate-400">
              {search
                ? t('categories.selector.noResults', 'Aucune catégorie trouvée')
                : t('categories.selector.empty', 'Aucune catégorie disponible')}
            </div>
          ) : (
            <div className="py-1">
              {searchResults.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => !category.hasChildren && handleSelect(category)}
                  disabled={category.hasChildren}
                  className={`w-full px-3 py-2 text-left flex items-center gap-2 ${
                    category.hasChildren 
                      ? 'cursor-not-allowed opacity-60' 
                      : 'hover:bg-violet-50 dark:hover:bg-violet-900/30 cursor-pointer'
                  } ${
                    value === category.id ? 'bg-violet-50 dark:bg-violet-900/30' : ''
                  }`}
                  style={{ paddingLeft: `${(category.level - 1) * 16 + 12}px` }}
                >
                  {category.level > 1 && (
                    <ChevronRightIcon />
                  )}
                  <span className="flex-shrink-0">
                    {category.logoUrl || <FolderIcon />}
                  </span>
                  <span className={`flex-1 truncate ${category.hasChildren ? 'text-slate-400 dark:text-slate-500 font-medium' : 'text-slate-700 dark:text-slate-200'}`}>
                    {category.name}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    category.baseType === 'credit'
                      ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400'
                      : 'bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400'
                  }`}>
                    {category.baseType === 'credit' ? 'C' : 'D'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
